import { gql, useMutation, useQuery } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { ECommunityVisibility } from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useEditGroupVisibility_GetGroupPermissionOptions,
  useEditGroupVisibility_GetGroupPermissionOptionsVariables,
} from './graphql-types/useEditGroupVisibility_GetGroupPermissionOptions';
import { useEditGroupVisibility_GroupFragment } from './graphql-types/useEditGroupVisibility_GroupFragment';
import {
  useEditGroupVisibility_UpdateGroupVisibility,
  useEditGroupVisibility_UpdateGroupVisibilityVariables,
} from './graphql-types/useEditGroupVisibility_UpdateGroupVisibility';

const UPDATE_GROUP_VISIBILTY = gql`
  mutation useEditGroupVisibility_UpdateGroupVisibility(
    $input: UpdateGroupCommunityVisibilityInput!
    $subgroupsInput: SubgroupsInput
  ) {
    updateGroupCommunityVisibility(input: $input) {
      data {
        _id
        settings {
          communityVisibility
        }
        subgroups(input: $subgroupsInput) {
          groupNodes {
            group {
              _id
              settings {
                communityVisibility
              }
            }
          }
        }
      }
    }
  }
`;

const GET_GROUP_VISIBILITY_OPTIONS = gql`
  query useEditGroupVisibility_GetGroupPermissionOptions(
    $input: GroupPermissionOptionsV2Input!
  ) {
    groupPermissionOptionsV2(input: $input) {
      data {
        validCommunityVisiblities
      }
    }
  }
`;

export const useEditGroupVisibility = (
  group: useEditGroupVisibility_GroupFragment
): {
  hideGroup: () => Promise<void>;
  canUnhideGroup: boolean;
  unhideGroup: () => Promise<void>;
} => {
  const permissions = usePermissions();
  const [updateGroup] = useMutation<
    useEditGroupVisibility_UpdateGroupVisibility,
    useEditGroupVisibility_UpdateGroupVisibilityVariables
  >(UPDATE_GROUP_VISIBILTY);

  const { data: groupPermissionOptions, loading } = useQuery<
    useEditGroupVisibility_GetGroupPermissionOptions,
    useEditGroupVisibility_GetGroupPermissionOptionsVariables
  >(GET_GROUP_VISIBILITY_OPTIONS, {
    variables: { input: { data: { parentGroupId: group.parentGroup?._id } } },
  });

  const toast = useToast();

  const isSubgroup = !!group.parentGroup?._id;

  const hideGroup = async (): Promise<void> => {
    if (!group) {
      return;
    }
    try {
      await updateGroup({
        variables: {
          input: {
            groupId: group._id,
            data: {
              communityVisibility: ECommunityVisibility.HIDDEN,
            },
          },
          subgroupsInput: {
            filter: permissions.canDoAdminStuffAndAuthenticated
              ? { includeAll: true }
              : undefined,
          },
        },
      });
      toast({
        title: `Successfully hid ${isSubgroup ? 'subgroup' : 'group'} "${
          group.name
        }".`,
        status: 'success',
      });
    } catch (e) {
      logError(e);
      toast({
        title: `Failed to hide ${isSubgroup ? 'subgroup' : 'group'} "${
          group.name
        }"`,
        status: 'error',
      });
    }
  };
  const unhideGroup = async (): Promise<void> => {
    if (!group) {
      return;
    }
    try {
      await updateGroup({
        variables: {
          input: {
            groupId: group._id,
            data: {
              communityVisibility: ECommunityVisibility.VISIBLE,
            },
          },
          subgroupsInput: {
            filter: permissions.canDoAdminStuffAndAuthenticated
              ? { includeAll: true }
              : undefined,
          },
        },
      });
      toast({
        title: `Successfully unhid ${isSubgroup ? 'subgroup' : 'group'} "${
          group.name
        }".`,
        status: 'success',
      });
    } catch (e) {
      logError(e);
      toast({
        title: `Failed to unhide ${isSubgroup ? 'subgroup' : 'group'} "${
          group.name
        }"`,
        status: 'error',
      });
    }
  };

  return {
    hideGroup,
    unhideGroup,
    canUnhideGroup:
      !loading &&
      !!groupPermissionOptions?.groupPermissionOptionsV2.data.validCommunityVisiblities.includes(
        ECommunityVisibility.VISIBLE
      ),
  };
};
useEditGroupVisibility.groupFragment = gql`
  fragment useEditGroupVisibility_GroupFragment on Group {
    _id
    name
    settings {
      communityVisibility
    }
    parentGroup {
      _id
    }
  }
`;
